/**
 * The barcode canvas, as a React component.
 *
 * WHY A useEffect + ref AND NOT JSX:
 * A <canvas> is an imperative pixel surface. React can create the element but cannot describe
 * its contents, so the draw has to happen in an effect against a ref, and it has to RE-RUN
 * whenever anything it read changes.
 *
 * The subtle part is COLOUR. A canvas inherits no CSS custom properties and no Cloudscape
 * design tokens — the pixels are whatever we wrote. So switching to light mode does not
 * recolour an already-drawn canvas: without `mode` in the dependency array the bars keep the
 * old colours and can end up invisible (white-on-white / black-on-black). That failure looks
 * completely fine in code review and only shows up on screen, so `mode` is a dependency and
 * verify-ui.js asserts the drawn contrast in BOTH modes.
 *
 * The renderer itself (web/barcode.js) is deliberately untouched by the Cloudscape port. It
 * snaps every bar boundary to a whole DEVICE pixel; a fractional module width antialiases each
 * edge into a grey ramp, and a scanner thresholds each column to bar/space, so a half-lit
 * boundary column can be assigned either way and corrupt an element width. Do not reintroduce
 * that bug by "scaling to fit".
 *
 * ─── TWO LAYOUT BUGS FIXED HERE. Do not undo either. ───────────────────────────────────────
 *
 * 1. AN EXPLICIT moduleWidth, NOT width:100%.
 *    The first version styled the canvas `width: 100%` inside an `inline-block` frame. That is a
 *    CIRCULAR width dependency: the frame sizes to its content, the content sizes to the frame,
 *    so `canvas.clientWidth` resolved to 0 and renderToCanvas fell back to `bits.length` — making
 *    the canvas exactly one device pixel per module. C04 (187 modules) therefore rendered 187px
 *    wide, C03 (242) rendered 242px, and only the narrow one misbehaved. Passing an explicit
 *    moduleWidth removes the dependency on layout entirely, which also removes a real race: an
 *    effect can run before layout settles and read a width of 0.
 *    It is also more spec-correct — the X-dimension of a barcode is a physical quantity that
 *    governs scannability, so a fixed module width beats "however wide the card happens to be".
 *
 * 2. NO CAPTION IS DRAWN INTO THE CANVAS.
 *    renderToCanvas can draw the human-readable line beneath the bars, and it did. But it does not
 *    measure the text, so with the collapsed 187px canvas above, C04's 27-character PIC (~211px at
 *    13px monospace) overflowed and rendered as a clipped `!12 3912 ... 0002 :`. On real mail a
 *    clipped human-readable PIC is a compliance failure, not a cosmetic nit.
 *    The caption is REDUNDANT — SerialCard already renders the same string from impb.js as a large
 *    heading directly below, where it cannot clip because it is ordinary reflowing text. So the
 *    canvas draws bars only. verify-ui.js asserts this (the canvas must have no caption band) and
 *    additionally measures the caption width against the canvas width, so if anyone reinstates it
 *    the check fails rather than the demo shipping clipped digits.
 */

import { useEffect, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import { Barcode } from '../lib/impb.js';
import { barcodeColors } from '../lib/theme.js';

/**
 * CSS px per module. 2px keeps every construct comfortably legible (C04 → 374px, C02 → 528px)
 * while staying an exact integer number of device pixels at dpr 1 and 2, so the crisp-edge
 * guarantee in renderToCanvas holds without rounding.
 */
const MODULE_WIDTH_PX = 2;

export default function BarcodeCanvas({ barcode, mode, barHeight = 74 }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !barcode) return;
    const colors = barcodeColors(mode);
    try {
      // drawImpb() would pass barcode.humanReadable through as `text`; call encode+render directly
      // so no caption is drawn. See note 2 in the header comment.
      const encoded = Barcode.encodeImpb(barcode);
      const render = Barcode.renderToCanvas(canvas, encoded.bits, {
        moduleWidth: MODULE_WIDTH_PX,
        barHeight,
        background: colors.background,
        foreground: colors.foreground,
        text: '',
      });
      const drawn = { encoded, render };
      // Record what was actually drawn so verify-ui.js can assert legibility numerically
      // instead of a human squinting at a screenshot.
      canvas.dataset.colorMode = mode;
      canvas.dataset.bgColor = colors.background;
      canvas.dataset.fgColor = colors.foreground;
      canvas.dataset.contrastRatio = colors.contrast.toFixed(2);
      setInfo({ ...drawn.encoded, contrast: colors.contrast });
      setError(null);
    } catch (err) {
      setError(err.message);
      setInfo(null);
    }
  }, [barcode, mode, barHeight]);

  if (error) {
    return (
      <Box color="text-status-error" variant="p">
        barcode render failed: {error}
      </Box>
    );
  }

  return (
    <div
      /*
       * A scanner needs DARK bars on a LIGHT background, so the symbol is NOT inverted for dark
       * mode — inverting would give a pretty barcode that a real scanner rejects. Instead the
       * frame renders as a piece of label stock sitting on the dark page, which also keeps the
       * quiet zones (mandatory, 10 modules each side) visually intact.
       */
      style={{
        background: barcodeColors(mode).background,
        padding: '12px 14px',
        borderRadius: '8px',
        display: 'inline-block',
        maxWidth: '100%',
        boxShadow: mode === 'dark' ? '0 0 0 1px rgba(255,255,255,0.25)' : '0 0 0 1px rgba(0,0,0,0.15)',
      }}
      data-testid="barcode-frame"
    >
      {/*
        NO `width: 100%` HERE. renderToCanvas sets the canvas's own width/height (both the pixel
        buffer and the CSS size) from the explicit moduleWidth. Restating a percentage width would
        re-create the circular dependency described in note 1 AND rescale the bitmap, which would
        smear the whole-device-pixel bar edges the renderer works to guarantee — silently undoing
        the crisp-edge fix that a scanner depends on.
      */}
      <canvas ref={canvasRef} style={{ display: 'block' }} data-symbol-modules={info ? info.totalModules : ''} />
    </div>
  );
}
